import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Task } from '../../../../types/task';
import { CalendarDayItem } from '../common/CalendarDayItem';
import { useAtom, useAtomValue } from 'jotai';
import {
  layoutModeAtom,
  sidebarCollapsedAtom,
  chatSidebarCollapsedAtom,
  chatSidebarWidthAtom
} from '../../../../atoms/v3/uiAtoms';
import { isDarkAtom } from '../../../../atoms/shared/uiAtoms';

interface DateSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  currentDate: Date;
  specialMode: 'past' | 'future' | 'notime' | 'recent' | null;
  onDateChange: (date: Date) => void;
  onSpecialModeChange: (mode: 'past' | 'future' | 'notime' | 'recent' | null) => void;
  tasks: Task[];
}

type QuickDateType = 'past' | 'yesterday' | 'today' | 'tomorrow' | 'future' | 'notime' | 'all' | 'recent';

/**
 * 日历选择器组件
 * 包含快速日期选择和迷你日历
 */
export const DateSelector: React.FC<DateSelectorProps> = ({
  isOpen,
  onClose,
  currentDate,
  specialMode,
  onDateChange,
  onSpecialModeChange,
  tasks
}) => {
  const { t, i18n } = useTranslation(['v3/tasks']);
  const [isDark] = useAtom(isDarkAtom);
  const layoutMode = useAtomValue(layoutModeAtom);
  const sidebarCollapsed = useAtomValue(sidebarCollapsedAtom);
  const chatSidebarCollapsed = useAtomValue(chatSidebarCollapsedAtom);
  const chatSidebarWidth = useAtomValue(chatSidebarWidthAtom);
  const [selectedQuickDate, setSelectedQuickDate] = useState<QuickDateType>('today');
  const [calendarDate, setCalendarDate] = useState(currentDate);

  // 当组件打开时，重置日历日期为当前选中日期
  useEffect(() => {
    if (isOpen) {
      setCalendarDate(currentDate);
    }
  }, [isOpen, currentDate]);

  // 根据当前状态同步 selectedQuickDate
  useEffect(() => {
    if (specialMode) {
      setSelectedQuickDate(specialMode);
    } else {
      // 判断当前日期是今天、昨天还是明天
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const currentDateNormalized = new Date(currentDate);
      currentDateNormalized.setHours(0, 0, 0, 0);

      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      if (currentDateNormalized.getTime() === today.getTime()) {
        setSelectedQuickDate('today');
      } else if (currentDateNormalized.getTime() === yesterday.getTime()) {
        setSelectedQuickDate('yesterday');
      } else if (currentDateNormalized.getTime() === tomorrow.getTime()) {
        setSelectedQuickDate('tomorrow');
      } else {
        // 其他日期不高亮任何快速选择按钮
        setSelectedQuickDate('today'); // 默认值，但不会高亮
      }
    }
  }, [specialMode, currentDate]);

  // 月份切换处理函数
  const handlePrevMonth = () => {
    const newDate = new Date(calendarDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCalendarDate(newDate);
  };

  const handleNextMonth = () => {
    const newDate = new Date(calendarDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCalendarDate(newDate);
  };

  // 获取今天、昨天、明天的日期
  const today = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);

  const yesterday = useMemo(() => {
    const date = new Date(today);
    date.setDate(date.getDate() - 1);
    return date;
  }, [today]);

  const tomorrow = useMemo(() => {
    const date = new Date(today);
    date.setDate(date.getDate() + 1);
    return date;
  }, [today]);

  // 快速日期选项
  const quickDateOptions: { key: QuickDateType; label: string; action: () => void }[] = [
    {
      key: 'past',
      label: t('dateSelector.pastAll'),
      action: () => {
        onSpecialModeChange('past');
        setSelectedQuickDate('past');
      }
    },
    {
      key: 'yesterday',
      label: t('dateSelector.yesterday'),
      action: () => {
        onDateChange(yesterday);
        onSpecialModeChange(null); // 清除特殊模式
        setSelectedQuickDate('yesterday');
      }
    },
    {
      key: 'today',
      label: t('dateSelector.today'),
      action: () => {
        onDateChange(today);
        onSpecialModeChange(null); // 清除特殊模式
        setSelectedQuickDate('today');
      }
    },
    {
      key: 'recent',
      label: t('dateSelector.recent'),
      action: () => {
        onSpecialModeChange('recent');
        setSelectedQuickDate('recent');
      }
    },
    {
      key: 'tomorrow',
      label: t('dateSelector.tomorrow'),
      action: () => {
        onDateChange(tomorrow);
        onSpecialModeChange(null); // 清除特殊模式
        setSelectedQuickDate('tomorrow');
      }
    },
    {
      key: 'future',
      label: t('dateSelector.futureAll'),
      action: () => {
        onSpecialModeChange('future');
        setSelectedQuickDate('future');
      }
    },
    {
      key: 'notime',
      label: t('dateSelector.noTime'),
      action: () => {
        onSpecialModeChange('notime');
        setSelectedQuickDate('notime');
      }
    },
    {
      key: 'all',
      label: t('dateSelector.allTasks'),
      action: () => {
        onSpecialModeChange('all');
        setSelectedQuickDate('all');
      }
    }
  ];

  // 计算当前月份的日历数据
  const calendarData = useMemo(() => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    
    // 获取当月第一天和最后一天
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // 获取第一天是星期几（0=周日）
    const firstDayOfWeek = firstDay.getDay();
    
    // 计算需要显示的日期范围
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDayOfWeek);
    
    const endDate = new Date(lastDay);
    const remainingDays = 6 - lastDay.getDay();
    endDate.setDate(endDate.getDate() + remainingDays);
    
    // 生成日期数组
    const dates: Date[] = [];
    const current = new Date(startDate);
    
    while (current <= endDate) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return {
      dates,
      currentMonth: month,
      currentYear: year
    };
  }, [calendarDate]);



  if (!isOpen) return null;

  // 桌面模式的样式调整
  const getContainerStyle = () => {
    if (layoutMode === 'desktop') {
      // 桌面模式：需要避让左右侧边栏
      return {
        position: 'fixed' as const,
        top: '48px',
        left: sidebarCollapsed ? '4.375rem' : '16.25rem', // 左侧边栏宽度
        right: chatSidebarCollapsed ? '0' : `${chatSidebarWidth}rem`, // 右侧边栏宽度
        zIndex: 50
      };
    } else {
      // 移动模式：全宽
      return {
        position: 'fixed' as const,
        top: '48px',
        left: '0',
        right: '0',
        zIndex: 50
      };
    }
  };

  // 遮罩层样式调整
  const getMaskStyle = () => {
    if (layoutMode === 'desktop') {
      // 桌面模式：避让左右侧边栏，从顶部开始遮挡
      return {
        position: 'fixed' as const,
        top: '0', // 从顶部开始遮挡
        bottom: '0',
        left: sidebarCollapsed ? '4.375rem' : '16.25rem',
        right: chatSidebarCollapsed ? '0' : `${chatSidebarWidth}rem`,
        zIndex: 40
      };
    } else {
      // 移动模式：全屏遮挡，但避开顶部栏
      return {
        position: 'fixed' as const,
        top: '64px', // 假设顶部栏高度为64px
        left: '0',
        right: '0',
        bottom: '0',
        zIndex: 40
      };
    }
  };

  return (
    <>
      {/* 遮罩层 - 根据布局模式调整 */}
      <div
        className="bg-black bg-opacity-50"
        style={getMaskStyle()}
        onClick={onClose}
      />

      {/* 滑下的日历面板 */}
      <div
        className={`
          transform transition-all duration-300 ease-out
          ${isDark ? 'bg-ios-dark-gray6' : 'bg-white'}
          ${isOpen ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}
        `}
        style={getContainerStyle()}
      >
        {/* 顶部快速选择标签 */}
        <div className="p-4">
          <div className="flex flex-wrap gap-2">
            {quickDateOptions.map(option => {
              // 计算是否应该高亮
              const shouldHighlight = (() => {
                if (specialMode) {
                  return selectedQuickDate === option.key && specialMode === option.key;
                } else {
                  return selectedQuickDate === option.key && !specialMode;
                }
              })();

              return (
                <button
                  key={option.key}
                  onClick={option.action}
                  className={`
                    px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                    ${shouldHighlight
                      ? 'bg-blue-500 text-white shadow-sm'
                      : isDark
                        ? 'bg-ios-dark-gray4 text-ios-dark-gray1 hover:bg-ios-dark-gray3 hover:shadow-sm'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100 hover:shadow-sm'
                    }
                  `}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* 迷你日历 */}
        <div className="px-4 pb-4">
          {/* 月份标题 */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={handlePrevMonth}
              className={`p-2 rounded-lg transition-colors ${
                isDark
                  ? 'hover:bg-ios-dark-gray4 text-ios-dark-gray1'
                  : 'hover:bg-gray-100 text-gray-600'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <h3 className={`text-lg font-semibold ${isDark ? 'text-ios-dark-gray1' : 'text-gray-900'}`}>
              {calendarDate.toLocaleDateString(i18n.language === 'zh' ? 'zh-CN' : 'en-US', {
                year: 'numeric',
                month: 'long'
              })}
            </h3>

            <button
              onClick={handleNextMonth}
              className={`p-2 rounded-lg transition-colors ${
                isDark
                  ? 'hover:bg-ios-dark-gray4 text-ios-dark-gray1'
                  : 'hover:bg-gray-100 text-gray-600'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* 星期标题 */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {[
              t('dateSelector.weekDays.sun'),
              t('dateSelector.weekDays.mon'),
              t('dateSelector.weekDays.tue'),
              t('dateSelector.weekDays.wed'),
              t('dateSelector.weekDays.thu'),
              t('dateSelector.weekDays.fri'),
              t('dateSelector.weekDays.sat')
            ].map(day => (
              <div
                key={day}
                className={`text-center text-xs font-medium py-1 ${
                  isDark ? 'text-ios-dark-gray2' : 'text-gray-500'
                }`}
              >
                {day}
              </div>
            ))}
          </div>

          {/* 日期网格 */}
          <div className="grid grid-cols-7 gap-0.5">
            {calendarData.dates.map((date, index) => {
              const isCurrentMonth = date.getMonth() === calendarData.currentMonth;

              return (
                <CalendarDayItem
                  key={index}
                  date={date}
                  currentDate={currentDate}
                  allTasks={tasks}
                  onClick={(selectedDate) => {
                    onDateChange(selectedDate);
                    onSpecialModeChange(null); // 清除特殊模式
                    onClose();
                  }}
                  showMonth={false}
                  isCurrentMonth={isCurrentMonth}
                  disableHighlight={!!specialMode} // 特殊模式下禁用高亮
                />
              );
            })}
          </div>
        </div>

      </div>
    </>
  );
};
